import { useState } from "react";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const CORRECT_PASSWORD = process.env.REACT_APP_DASHBOARD_PASSWORD || "CargoDashTargets@1";

export default function PasswordGate({ onUnlock }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    // Small delay for UX
    setTimeout(() => {
      if (password === CORRECT_PASSWORD) {
        sessionStorage.setItem("cargodash_authenticated", "true");
        onUnlock();
      } else {
        setError("Incorrect password. Please try again.");
        setPassword("");
      }
      setIsLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.svg" 
              alt="CargoDash" 
              className="h-12"
            />
          </div>

          {/* Lock Icon */}
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-[#0891B2]/10 rounded-full flex items-center justify-center">
              <Lock className="w-8 h-8 text-[#0891B2]" />
            </div>
          </div>

          {/* Title */}
          <h1 className="text-xl font-semibold text-center text-slate-900 mb-2">
            Protected Dashboard
          </h1>
          <p className="text-sm text-slate-500 text-center mb-6">
            Enter your password to access CargoDash
          </p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pr-10 bg-slate-50 border-slate-200 text-slate-900 placeholder:text-slate-400 h-12"
                data-testid="password-input"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                {showPassword ? (
                  <EyeOff className="w-5 h-5" />
                ) : (
                  <Eye className="w-5 h-5" />
                )}
              </button>
            </div>

            {error && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-sm text-red-500 text-center"
                data-testid="password-error"
              >
                {error}
              </motion.p>
            )}

            <Button
              type="submit"
              disabled={!password || isLoading}
              className="w-full h-12 bg-[#0891B2] hover:bg-[#0E7490] text-white font-semibold"
              data-testid="unlock-button"
            >
              {isLoading ? "Verifying..." : "Unlock Dashboard"}
            </Button>
          </form>
        </div>

        <p className="text-xs text-slate-400 text-center mt-4">
          CargoDash Logistics Dashboard
        </p>
      </motion.div>
    </div>
  );
}
